import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as moment from 'moment';
import { renderToStaticMarkup } from 'react-dom/server';
import Title from '../../commonComponents/Title';
import {
  formatPrice,
  ORDER_STATUSES,
  BUYER_TYPE,
  BUYER_TYPES,
  ACADEMY_ORDER_STATUSES,
  SUBSCRIPTION_STATUS,
  findCurrencySymbol,
  displayUserName,
} from '../../services/utilityFunctions';
import translate from '../../commonComponents/translate';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import FormInput from '../../commonComponents/forms/FormInput';
import CurrencyProp from '../profile/propTypes/currencyProp';
import DateRange from '../../commonComponents/forms/inputs/DateRange';
import Anchor from '../../commonComponents/Anchor';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import Carousel from '../../commonComponents/carousel/Carousel';
import ServerSideCarousel from '../../commonComponents/carousel/ServerSideCarousel';
import ConfigsContext from '../../contextProviders/ConfigsContext';
import { PAYMENT_PROVIDER } from '../../commonComponents/propTypes/tenantProp';
import ServerSideList from '../../commonComponents/lists/ServerSideList';

function ShopUserOrders({ t, currencies, directSellingActive, isAcademy }) {
  const [query, setQuery] = useState({ search: '', buyerType: 'owner' });
  const [filters, setFilters] = useState({ startDate: null, endDate: null });
  const { loggedUser } = useContext(LoggedUserContext);
  const { tenant } = useContext(ConfigsContext);
  const tableRef = useRef(null);
  const isPaymentProviderNone =
    tenant?.settings?.paymentProviderType === PAYMENT_PROVIDER.NONE;
  const showNewOrderBtn =
    !isPaymentProviderNone &&
    !isAcademy &&
    directSellingActive &&
    (loggedUser.role === 'admin' || loggedUser.type === 'collaborator');

  const statusText = status => t(`shop.${status}`);

  const buyerTypeText = buyerType => t(`shop.buyer_type.${buyerType}`);

  const updateQueryStatus = value => {
    setQuery(prev => ({
      ...prev,
      status: value === '0' ? '' : value,
    }));
  };

  const updateQueryBuyerType = value => {
    if (tableRef?.current) {
      tableRef.current.destroy();
    }
    setQuery(prev => ({
      ...prev,
      buyerType: value === '0' ? '' : value,
    }));
    if (tableRef?.current) {
      tableRef.current.mountTable();
    }
  };

  const updateQuerySearch = value => {
    setQuery(prev => ({
      ...prev,
      search: value === '0' ? '' : value,
    }));
  };

  const handleDateChange = (startDate, endDate) => {
    setFilters(prevState => ({ ...prevState, startDate, endDate }));
  };

  const columns = [
    {
      field: 'number',
      textAlign: 'left',
      title: t('orders_list_number'),
      width: 100,
      sortable: 'desc',
      template: item =>
        renderToStaticMarkup(
          <span className={`order-status ${item.status}`}>{item.number}</span>,
        ),
    },
    {
      field: 'createdAt',
      textAlign: 'left',
      title: t('shop.date'),
      width: 100,
      sortable: true,
      template: item => moment(item.createdAt).format('DD MMM, YYYY'),
    },
    {
      field: 'price',
      textAlign: 'left',
      title: t('shop.price'),
      width: 80,
      template: ({ total, currency }) =>
        formatPrice(total, findCurrencySymbol(currency, currencies) || '€'),
    },
    {
      field: 'status',
      textAlign: 'left',
      title: t('shop.status'),
      width: 100,
      template: item =>
        renderToStaticMarkup(
          <span className={`order-status ${item.status}`}>
            {statusText(item.status)}
          </span>,
        ),
    },
    {
      field: 'tax',
      textAlign: 'left',
      title: t('shop.order.detail.vat'),
      width: 100,
      template: item =>
        formatPrice(
          item.subtotalTax,
          findCurrencySymbol(item.currency, currencies) || '€',
        ),
    },
    {
      field: 'invoice',
      textAlign: 'left',
      title: t('shop.invoice'),
      width: 120,
      template: item =>
        renderToStaticMarkup(
          item.invoice?.number ? (
            <a
              href={`/shop/invoice/${item._id}?type=${
                isAcademy ? 'academy' : 'market'
              }`}
              target="_blank"
              rel="noreferrer"
            >
              {item.invoice.number}
            </a>
          ) : (
            <>N/A</>
          ),
        ),
    },
  ];

  if (query.buyerType !== BUYER_TYPE.OWNER) {
    columns.unshift({
      field: 'owner',
      textAlign: 'left',
      title: t('shop.client'),
      width: 100,
      template: item =>
        renderToStaticMarkup(<>{displayUserName(item.owner)}</>),
    });
  }

  let statusItems;
  if (isAcademy) {
    statusItems = [
      { text: t('all'), id: '0' },
      ...ACADEMY_ORDER_STATUSES.map(s => ({
        text: statusText(s.status),
        id: s.status,
      })),
    ];
  } else {
    statusItems = [
      { text: t('all'), id: '0' },
      ...ORDER_STATUSES.map(s => ({
        text: statusText(s.status),
        id: s.status,
      })),
    ];
  }
  const subscriptionsOrdersUrl = '/table/market/order/subscription/list';
  const dataTableUrl = !isAcademy
    ? '/table/market/order/list'
    : '/table/academy/order/list';

  return (
    <Title title={t('header_orders')}>
      <ContentContainer>
        <SubHeader title={t('header_orders')} />
        <div className="tab-order-container">
          <div className="white-bar_container tabs">
            <div className="col-lg-12">
              <a
                href="/shop/order"
                className={!isAcademy ? 'academy-tab-active' : ''}
              >
                <span className="white-bar_container-tabs">
                  {t('commissions.marketplace')}
                </span>
              </a>
              <a
                href="/shop/academy/order"
                className={isAcademy ? 'academy-tab-active' : ''}
              >
                <span className="white-bar_container-tabs">
                  {t('academy_head_title')}
                </span>
              </a>
            </div>
          </div>
        </div>
        <div className="m-content shop">
          <div className="row no-gutters">
            <div className="col-lg-12 user-orders-list">
              <div className="row">
                <div className="row col-lg-10 col-12">
                  <FormInput
                    type="text"
                    onChange={updateQuerySearch}
                    id="search-input"
                    placeholder={t('shop.search')}
                    groupProps={{
                      className: 'col-lg-3',
                    }}
                  />
                  <FormInput
                    type="select"
                    onChange={updateQueryStatus}
                    id="status-order"
                    placeholder={t('shop.status')}
                    value=""
                    groupProps={{
                      className: 'col-lg-3',
                    }}
                    inputProps={{
                      items: statusItems,
                      allowClear: true,
                    }}
                  />
                  {loggedUser.type === 'collaborator' && !isAcademy && (
                    <FormInput
                      type="select"
                      onChange={updateQueryBuyerType}
                      id="buyer-type"
                      placeholder={t('shop.buyer_type')}
                      value={query.buyerType}
                      groupProps={{
                        className: 'col-lg-3',
                      }}
                      inputProps={{
                        items: BUYER_TYPES.map(s => ({
                          text: buyerTypeText(s),
                          id: s,
                        })),
                      }}
                    />
                  )}
                  <div className="col-lg-2 col-md-2 col-sm-2">
                    <DateRange
                      placeholder={t('shop.date')}
                      startDate={filters.startDate}
                      endDate={filters.endDate}
                      onChange={({ startDate, endDate }) => {
                        handleDateChange(startDate, endDate);
                      }}
                      onClear={() => {
                        handleDateChange(null, null);
                      }}
                    />
                  </div>
                </div>
                {showNewOrderBtn && (
                  <div className="col-lg-2 col-md-2 col-sm-2 order-create-btn">
                    <Anchor
                      href="/shop/order/create"
                      className="btn m-btn--pill btn-dynamic"
                    >
                      {t('order.create.button')}
                    </Anchor>
                  </div>
                )}
              </div>
              {!isAcademy && (
                <div className="row no-gutters">
                  <div className="col-12">
                    <h5 className="subscriptions-title">
                      {t('order.subscriptions')}
                    </h5>
                    <Carousel>
                      <ServerSideCarousel
                        id="my-subscriptions-carousel"
                        fetchUrl={subscriptionsOrdersUrl}
                        defaultFetchError="error_fetchMySubscriptions"
                        fetchHeaders={{
                          'Accept-Version': '1.0.0',
                          'X-Gamification-API': 'Games',
                          'Content-Type': 'application/json',
                        }}
                        fetchQuery={{
                          ...filters,
                          ...query,
                          status: [
                            SUBSCRIPTION_STATUS.ACTIVE,
                            SUBSCRIPTION_STATUS.PENDING_PAYMENT,
                            SUBSCRIPTION_STATUS.OUT_OF_STOCK,
                          ],
                        }}
                      >
                        {items =>
                          items.length > 0 ? (
                            items.map(subscription => {
                              // get the first product image
                              const image =
                                subscription?.productParent?.images[0] || null;
                              const imageURL =
                                image && image.thumb
                                  ? image.thumb
                                  : '/images/no_image_upload.png';
                              return (
                                <Anchor
                                  href={`/shop/order/subscription/${subscription._id}`}
                                  className="my-subscriptions"
                                >
                                  <div className="my-subscriptions-product-thumbnail">
                                    <div className="product-thumbnail-item">
                                      <img
                                        src={imageURL}
                                        alt={
                                          subscription?.productParent?.name ||
                                          'N/A'
                                        }
                                      />
                                    </div>
                                  </div>
                                  <div className="my-subscriptions-info">
                                    <div className="my-subscriptions-info-productName">
                                      <span className="title">
                                        {subscription?.productParent?.name ||
                                          'N/A'}
                                      </span>
                                      <span className="value">
                                        {`${formatPrice(
                                          subscription.total,
                                          findCurrencySymbol(
                                            subscription.currency,
                                            currencies,
                                          ) || '€',
                                        )}/ ${t(
                                          `order.subscription.${subscription.frequency}`,
                                        )}`}
                                      </span>
                                    </div>
                                    <div className="my-subscriptions-info-nextOrder">
                                      <span className="title">
                                        {t('order.next_order')}
                                      </span>
                                      <span className="value">
                                        {moment
                                          .utc(subscription.nextPaymentDate)
                                          .format('DD MMMM YYYY')}
                                      </span>
                                    </div>
                                  </div>
                                </Anchor>
                              );
                            })
                          ) : (
                            <div>{t('order.noSubscriptions')}</div>
                          )
                        }
                      </ServerSideCarousel>
                    </Carousel>
                  </div>
                </div>
              )}
              <div className="row no-gutters">
                <div className="col-12">
                  <h5 className="orders-title">{t('orders_head_title')}</h5>
                  <ServerSideList
                    ref={tableRef}
                    columns={columns}
                    id="orders-list"
                    dataSourceURL={dataTableUrl}
                    query={{
                      ...filters,
                      ...query,
                      populate:
                        query.buyerType !== BUYER_TYPE.OWNER
                          ? 'owner'
                          : undefined,
                    }}
                    onRowClick={(item, column) => {
                      if (item) {
                        if (column && column === 'invoice') {
                          return;
                        }
                        window.location = isAcademy
                          ? `${item._id}`
                          : `order/${item._id}`;
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </ContentContainer>
    </Title>
  );
}

ShopUserOrders.propTypes = {
  t: PropTypes.func.isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  directSellingActive: PropTypes.bool,
  isAcademy: PropTypes.bool,
};

ShopUserOrders.defaultProps = {
  directSellingActive: false,
  isAcademy: false,
};

export default translate(ShopUserOrders);
